import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../../services/api";
import "./holiday.css";

function daysBetween(start, end) {
  const ms = new Date(end) - new Date(start);
  return Math.max(1, Math.round(ms / 86400000) + 1);
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
}

export default function HolidayList() {
  const school   = JSON.parse(localStorage.getItem("schoolAdmin"));
  const navigate = useNavigate();

  const [holidays, setHolidays] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({ title:"", start_date:"", end_date:"", description:"" });
  const [saving,   setSaving]   = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = async () => {
    try {
      setLoading(true);
      const res = await API.get("/school-admin/holidays/", {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setHolidays(res.data.holidays || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (h) => {
    setEditing(h);
    setForm({ title: h.title, start_date: h.start_date, end_date: h.end_date, description: h.description || "" });
  };

  const saveEdit = async () => {
    if (!form.title.trim() || !form.start_date || !form.end_date)
      return alert("Title and dates are required");
    try {
      setSaving(true);
      await API.post(`/school-admin/holidays/update/${editing.id}/`, form, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setEditing(null);
      load();
    } catch (err) { console.error(err); alert("Failed to update holiday"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;
    try {
      await API.post(`/school-admin/holidays/delete/${id}/`, {}, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setHolidays(h => h.filter(x => x.id !== id));
    } catch (err) { console.error(err); alert("Failed to delete"); }
  };

  return (
    <div className="hl-page">

      {/* ══ HERO ══ */}
      <div className="hl-hero">
        <div className="hl-hero-left">
          <div className="hl-hero-icon">🎉</div>
          <div>
            <h2 className="hl-hero-title">Holidays</h2>
            <p className="hl-hero-sub">
              {loading ? "Loading…" : `${holidays.length} holiday${holidays.length !== 1 ? "s" : ""} scheduled`}
            </p>
          </div>
        </div>
        <div className="hl-hero-right">
          <button className="hl-btn-add" onClick={() => navigate("/school-admin/holidays/add")}>
            + Add Holiday
          </button>
        </div>
      </div>

      {/* ══ TABLE ══ */}
      <div className="hl-card">
        {loading ? (
          <div className="hl-empty">
            <div className="hl-empty-icon">⏳</div>
            <strong>Loading holidays…</strong>
          </div>
        ) : holidays.length === 0 ? (
          <div className="hl-empty">
            <div className="hl-empty-icon">📅</div>
            <strong>No holidays yet</strong>
            <p style={{ marginTop:8, fontSize:13 }}>Add your first school holiday</p>
          </div>
        ) : (
          <table className="hl-table">
            <thead>
              <tr>
                <th>Holiday</th>
                <th>Date Range</th>
                <th>Duration</th>
                <th>Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {holidays.map(h => (
                <tr key={h.id}>
                  <td><span className="hl-title-cell">{h.title}</span></td>
                  <td>
                    <div className="hl-date-range">
                      <span className="hl-date-start">{fmtDate(h.start_date)}</span>
                      {h.start_date !== h.end_date && (
                        <>
                          <span className="hl-date-sep">to</span>
                          <span className="hl-date-end">{fmtDate(h.end_date)}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="hl-duration">
                      {daysBetween(h.start_date, h.end_date)} day{daysBetween(h.start_date, h.end_date) !== 1 ? "s" : ""}
                    </span>
                  </td>
                  <td><span className="hl-desc">{h.description || "—"}</span></td>
                  <td style={{ whiteSpace:"nowrap" }}>
                    <button className="hl-btn-edit" onClick={() => openEdit(h)}>✏️ Edit</button>
                    <button className="hl-btn-del"  onClick={() => del(h.id)}>🗑 Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ══ EDIT MODAL ══ */}
      {editing && (
        <div className="hl-modal-overlay" onClick={() => setEditing(null)}>
          <div className="hl-modal" onClick={e => e.stopPropagation()}>
            <div className="hl-modal-head">
              <div className="hl-modal-head-icon">✏️</div>
              <h3>Edit Holiday</h3>
            </div>
            <div className="hl-modal-grid">
              <div className="hl-modal-field hl-modal-field-full">
                <label>Title *</label>
                <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Holiday name" autoFocus />
              </div>
              <div className="hl-modal-field">
                <label>Start Date *</label>
                <input type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} />
              </div>
              <div className="hl-modal-field">
                <label>End Date *</label>
                <input type="date" value={form.end_date} onChange={e => set("end_date", e.target.value)} />
              </div>
              <div className="hl-modal-field hl-modal-field-full">
                <label>Description</label>
                <textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="Optional note…" />
              </div>
            </div>
            <div className="hl-modal-actions">
              <button className="hl-modal-cancel" onClick={() => setEditing(null)}>Cancel</button>
              <button className="hl-modal-save"   onClick={saveEdit} disabled={saving}>
                {saving ? <><div className="hl-spinner"></div> Saving…</> : "✓ Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
